import { useCallback, useMemo } from 'react';

import { usePreviewQuery } from './hooks/usePreviewQuery';
import { useUpdateQuery } from './hooks/useUpdateQuery';
import { NoFiltersMessage } from './NoFiltersMessage';
import { PageHeader } from './PageHeader';
import { PreviewPostList } from './PreviewPostList';
import { hasSavedQueryChanged } from './utils/filterComparison';
import { isFilterEmpty } from './utils/validationLogic';

import { type SavedQuery, type SavedQueryBody, LogicalOperator } from '@/entities/query/types';
import { Button } from '@/shared/components/shadcn/button';

interface Props {
  savedQuery: SavedQuery;
  currentPaylaod: SavedQueryBody;
  workspaceId: string;
  onClose?: () => void;
}

export function QueryPreview({ savedQuery, currentPaylaod, workspaceId, onClose }: Props) {
  const updateQuery = useUpdateQuery();

  const hasFilterChanged = useMemo(() => {
    return hasSavedQueryChanged(currentPaylaod, savedQuery);
  }, [currentPaylaod, savedQuery]);

  const hasNoFilters = useMemo(() => {
    return isFilterEmpty(currentPaylaod.filter);
  }, [currentPaylaod]);

  // Prepare query params only when filter is not empty
  const queryParams = useMemo(() => {
    if (isFilterEmpty(currentPaylaod.filter)) return null;

    return {
      workspaceId,
      queryId: savedQuery.id,
      ...currentPaylaod,
    };
  }, [currentPaylaod, workspaceId, savedQuery.id]);

  const { data, isLoading } = usePreviewQuery(queryParams);

  const handleSaveFilter = useCallback(async () => {
    // Trim filter values before saving
    const excludeValues =
      currentPaylaod?.filter?.exclude?.values?.filter((v) => v.value.trim() !== '') || [];

    const trimmedFilter: SavedQueryBody = {
      ...currentPaylaod,
      filter: {
        ...currentPaylaod.filter,
        include: currentPaylaod.filter.include.map((include) => {
          const values = include.values.filter((v) => v.value.trim() !== '');
          return {
            op: values.length > 0 ? include.op : LogicalOperator.None,
            values: values.length > 0 ? values : [],
          };
        }),
        exclude: {
          op: excludeValues.length > 0 ? currentPaylaod.filter.exclude.op : LogicalOperator.None,
          values: excludeValues.length > 0 ? excludeValues : [],
        },
      },
    };

    await updateQuery.mutateAsync({
      workspaceId,
      queryId: savedQuery.id,
      ...trimmedFilter,
    });

    onClose?.();
  }, [currentPaylaod, savedQuery.id, updateQuery, workspaceId, onClose]);

  return (
    <div className="flex w-[640px] flex-col overflow-hidden bg-white">
      <PageHeader
        title="Query preview"
        description="Displaying up to 10 sample posts that match your filter."
        right={
          <Button onClick={handleSaveFilter} disabled={!hasFilterChanged || updateQuery.isPending}>
            Save filter
          </Button>
        }
      />
      <div className="flex-1 overflow-y-auto">
        {hasNoFilters ? (
          <NoFiltersMessage />
        ) : (
          <PreviewPostList posts={data?.posts ?? []} isLoading={isLoading} />
        )}
      </div>
    </div>
  );
}
